import { useMemo, useState, type ReactNode } from 'react'
import { useDebounce } from '../../hooks/useDebounce'
import { Button } from './Button'
import { EmptyState } from './EmptyState'
import { Input } from './Input'

export interface DataTableColumn<T> {
  header: string
  cell: (row: T) => ReactNode
  className?: string
  /** Si se pasa, el encabezado se vuelve clicable para ordenar la tabla por esta columna. */
  sortValue?: (row: T) => number | string
}

interface DataTableProps<T> {
  data: T[]
  columns: DataTableColumn<T>[]
  rowKey: (row: T) => string
  pageSize?: number
  emptyMessage?: string
  /** Si se pasa, muestra un buscador que filtra por coincidencia (sin distinguir mayúsculas) contra este texto. */
  getSearchText?: (row: T) => string
  searchPlaceholder?: string
}

export function DataTable<T>({
  data,
  columns,
  rowKey,
  pageSize = 10,
  emptyMessage = 'Nada registrado todavía.',
  getSearchText,
  searchPlaceholder = 'Buscar...',
}: DataTableProps<T>) {
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search)
  const [sort, setSort] = useState<{ header: string; dir: 'asc' | 'desc' } | null>(null)

  const filtered = useMemo(() => {
    const term = debouncedSearch.trim().toLowerCase()
    if (!term || !getSearchText) return data
    return data.filter((row) => getSearchText(row).toLowerCase().includes(term))
  }, [data, debouncedSearch, getSearchText])

  const sorted = useMemo(() => {
    if (!sort) return filtered
    const col = columns.find((c) => c.header === sort.header)
    if (!col?.sortValue) return filtered
    const dir = sort.dir === 'asc' ? 1 : -1
    return [...filtered].sort((a, b) => {
      const av = col.sortValue!(a)
      const bv = col.sortValue!(b)
      if (av < bv) return -1 * dir
      if (av > bv) return 1 * dir
      return 0
    })
  }, [filtered, sort, columns])

  function toggleSort(header: string) {
    setSort((current) => {
      if (current?.header !== header) return { header, dir: 'asc' }
      if (current.dir === 'asc') return { header, dir: 'desc' }
      return null
    })
    setPage(1)
  }

  const pageCount = Math.max(1, Math.ceil(sorted.length / pageSize))
  const currentPage = Math.min(page, pageCount)
  const pageRows = useMemo(
    () => sorted.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    [sorted, currentPage, pageSize],
  )

  if (data.length === 0) return <EmptyState message={emptyMessage} />

  return (
    <div>
      {getSearchText && (
        <div className="relative mb-3 max-w-xs">
          <Input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              setPage(1)
            }}
            placeholder={searchPlaceholder}
            className="pr-8"
            aria-label={searchPlaceholder}
          />
          {search && (
            <button
              type="button"
              onClick={() => {
                setSearch('')
                setPage(1)
              }}
              title="Limpiar búsqueda"
              aria-label="Limpiar búsqueda"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted hover:text-text"
            >
              ×
            </button>
          )}
        </div>
      )}
      {filtered.length === 0 ? (
        <EmptyState message="No hay resultados para esa búsqueda." />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-left text-sm">
            <thead>
              <tr className="border-line border-b text-xs text-muted uppercase">
                {columns.map((col) =>
                  col.sortValue ? (
                    <th key={col.header} className={`py-2 pr-3 font-semibold ${col.className ?? ''}`}>
                      <button
                        type="button"
                        onClick={() => toggleSort(col.header)}
                        className="flex items-center gap-1 uppercase text-muted hover:text-text"
                      >
                        {col.header}
                        <span className="text-[10px]">
                          {sort?.header === col.header ? (sort.dir === 'asc' ? '▲' : '▼') : '⇅'}
                        </span>
                      </button>
                    </th>
                  ) : (
                    <th key={col.header} className={`py-2 pr-3 font-semibold ${col.className ?? ''}`}>
                      {col.header}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody>
              {pageRows.map((row) => (
                <tr key={rowKey(row)} className="border-line border-b last:border-b-0">
                  {columns.map((col) => (
                    <td key={col.header} className={`py-2.5 pr-3 align-middle ${col.className ?? ''}`}>
                      {col.cell(row)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {pageCount > 1 && (
        <div className="mt-3 flex items-center justify-between text-sm text-muted">
          <span>
            Página {currentPage} de {pageCount} · {filtered.length} registros
          </span>
          <div className="flex gap-2">
            <Button variant="secondary" size="sm" disabled={currentPage <= 1} onClick={() => setPage((p) => p - 1)}>
              ‹ Anterior
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={currentPage >= pageCount}
              onClick={() => setPage((p) => p + 1)}
            >
              Siguiente ›
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
